import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, X } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const TERMS = [
  {
    term: "Above the Line Voting",
    letter: "A",
    definition:
      "In Senate elections, voting by numbering boxes above the thick line for parties or groups. Voters must number at least 6 boxes. The party then distributes preferences according to their registered list.",
    example: "Writing '1' above the line for the Greens votes for their entire Senate ticket.",
    related: ["Below the Line Voting", "Senate", "Preferential Voting"],
  },
  {
    term: "Australian Electoral Commission (AEC)",
    letter: "A",
    definition:
      "The independent statutory authority responsible for conducting federal elections and referendums in Australia. The AEC manages electoral rolls, polling places, and counting.",
    example: "You enrol to vote, check your enrolment, and find your polling booth via the AEC website.",
    related: ["Electoral Roll", "Polling Booth"],
  },
  {
    term: "Below the Line Voting",
    letter: "B",
    definition:
      "In Senate elections, numbering every individual candidate in the order you choose below the thick line. Voters must number at least 12 candidates. Gives full control over preferences.",
    example: "Numbering all 80+ Senate candidates gives you complete control over your preference flow.",
    related: ["Above the Line Voting", "Senate", "Preferential Voting"],
  },
  {
    term: "By-election",
    letter: "B",
    definition:
      "An election held for a single seat outside the regular election cycle, usually triggered when a sitting member resigns, dies, or is disqualified.",
    example: "The 2018 Wentworth by-election, triggered by Malcolm Turnbull's resignation, almost cost the Coalition government its majority.",
    related: ["Casual Vacancy", "Electoral Roll"],
  },
  {
    term: "Casual Vacancy",
    letter: "C",
    definition:
      "A Senate vacancy that arises when a senator leaves before their term ends. Filled by the relevant state parliament nominating a replacement from the same party.",
    example: "When a Labor senator resigns, the state parliament nominates another Labor member to fill the remaining term.",
    related: ["Senate", "By-election"],
  },
  {
    term: "Coalition",
    letter: "C",
    definition:
      "The political alliance between the Liberal Party and The Nationals (and state affiliates). They contest elections under a joint agreement and form government together when they win.",
    example: "The Coalition governed Australia from 2013–2022 under PMs Abbott, Turnbull, Morrison.",
    related: ["Liberal Party", "The Nationals", "Major Parties"],
  },
  {
    term: "Compulsory Voting",
    letter: "C",
    definition:
      "In Australia, all eligible citizens are legally required to enrol and vote in federal and most state elections. Failure to vote can result in a fine (currently $20 for first offence federally).",
    example: "Australia has one of the world's highest voter turnout rates (~90%) because of compulsory voting.",
    related: ["Electoral Roll", "Informal Vote"],
  },
  {
    term: "Crossbench",
    letter: "C",
    definition:
      "Senators or MPs who are neither in the government nor the official opposition. Includes minor party members and independents. In the Senate, the crossbench often holds significant power.",
    example: "The Greens, One Nation, and various independents sit on the Senate crossbench.",
    related: ["Independent", "Minor Party", "Senate"],
  },
  {
    term: "Distribution of Preferences",
    letter: "D",
    definition:
      "The process in preferential voting where votes for candidates who are eliminated are transferred to the next ranked choice on each ballot until one candidate has a majority.",
    example: "If your first choice (Greens) is eliminated, your vote is passed to your second preference (ALP).",
    related: ["Preferential Voting", "Two-Party Preferred"],
  },
  {
    term: "Division",
    letter: "D",
    definition:
      "The official term for a federal electoral district. Also called an electorate or seat. Australia has 151 federal divisions for the House of Representatives.",
    example: "Sydney, Melbourne, Perth and Brisbane are the names of specific federal divisions.",
    related: ["Electorate", "House of Representatives"],
  },
  {
    term: "Donkey Vote",
    letter: "D",
    definition:
      "A ballot where the voter simply numbers candidates from top to bottom (1, 2, 3...) in the order they appear on the ballot paper, rather than based on preference.",
    example: "Donkey votes can benefit candidates who are listed first on the ballot, which is why ballots are often randomised.",
    related: ["Informal Vote", "Preferential Voting"],
  },
  {
    term: "Double Dissolution",
    letter: "D",
    definition:
      "A constitutional mechanism allowing the Governor-General to dissolve both Houses of Parliament simultaneously. Used when the Senate twice rejects or fails to pass a bill from the House of Representatives.",
    example: "PM Malcolm Turnbull called a double dissolution election in 2016 over the ABCC legislation.",
    related: ["Senate", "House of Representatives", "Governor-General"],
  },
  {
    term: "Electoral Roll",
    letter: "E",
    definition:
      "The official list of all eligible voters. Australians aged 18+ must be enrolled to vote. The roll closes 7 days after an election is called.",
    example: "Check or update your enrolment at the AEC website. Enrolment closes ~7 days after an election is called.",
    related: ["AEC", "Compulsory Voting", "Polling Booth"],
  },
  {
    term: "Electorate",
    letter: "E",
    definition:
      "A geographic area represented by one Member of Parliament. Also called a seat or division. Federal electorates have roughly equal populations (~110,000 voters each).",
    example: "'The seat of Melbourne' or 'the electorate of Melbourne' refers to the inner-city Melbourne federal division.",
    related: ["Division", "Marginal Seat", "Safe Seat"],
  },
  {
    term: "First Preference",
    letter: "F",
    definition:
      "The candidate marked as '1' on your ballot — your top choice. Also called the primary vote. In federal elections, a party's first preference share is a key measure of their support.",
    example: "The ALP's first preference vote in 2025 was about 35%, but they received more votes after preferences.",
    related: ["Primary Vote", "Preferential Voting", "Two-Party Preferred"],
  },
  {
    term: "Gerrymander",
    letter: "G",
    definition:
      "Deliberately drawing electoral boundaries to advantage a particular party or group. Less of an issue in Australia due to independent redistribution processes managed by the AEC.",
    example: "Australia uses independent electoral redistributions to prevent gerrymandering.",
    related: ["Redistribution", "Electorate", "AEC"],
  },
  {
    term: "Governor-General",
    letter: "G",
    definition:
      "The representative of the King in Australia, who has the formal power to appoint prime ministers, dissolve parliament, and call elections. Normally acts on ministerial advice.",
    example: "In 1975, Governor-General Sir John Kerr controversially dismissed PM Gough Whitlam's government.",
    related: ["Double Dissolution", "Prime Minister"],
  },
  {
    term: "Hung Parliament",
    letter: "H",
    definition:
      "When no single party or coalition wins a majority of seats (76 of 151 in the House). Leads to negotiations with independents or minor parties to form government.",
    example: "Australia had a hung parliament in 2010 after the federal election, with the Gillard Labor government relying on independents and Greens to govern.",
    related: ["Crossbench", "Minority Government", "Confidence and Supply"],
  },
  {
    term: "Independent",
    letter: "I",
    definition:
      "A candidate or MP who is not affiliated with any registered political party. Independents are elected on their personal platform and community support.",
    example: "Zali Steggall (Warringah), Andrew Wilkie (Clark), and the Teal independents all sit as federal independents.",
    related: ["Crossbench", "Teal Independents", "Minor Party"],
  },
  {
    term: "Informal Vote",
    letter: "I",
    definition:
      "A ballot paper that has not been filled in correctly and cannot be counted. This includes unmarked ballots, duplicate numbers, or too few preferences. Informal votes are not counted toward any candidate.",
    example: "About 5% of federal ballots are informal. You can lodge a 'formal informal' vote by deliberately submitting a blank ballot.",
    related: ["Compulsory Voting", "Donkey Vote", "Preferential Voting"],
  },
  {
    term: "Lower House",
    letter: "L",
    definition:
      "The House of Representatives — the primary chamber of parliament. Government is formed in the lower house. The party or coalition with the most lower house seats forms government.",
    example: "Australia's lower house has 151 seats. A party needs 76 to hold a majority.",
    related: ["House of Representatives", "Upper House", "Senate"],
  },
  {
    term: "Marginal Seat",
    letter: "M",
    definition:
      "An electorate where the margin between the winning and second-placed candidate is less than ~6%. These seats are targeted heavily by all parties at election time.",
    example: "Bass (TAS) and Reid (NSW) are classic marginal seats that often predict who wins government.",
    related: ["Safe Seat", "Swing", "Two-Party Preferred"],
  },
  {
    term: "Minor Party",
    letter: "M",
    definition:
      "A registered political party other than the major parties (ALP, Coalition). Includes the Greens, One Nation, United Australia Party, Jacqui Lambie Network, etc.",
    example: "The Greens are the largest minor party, holding 11+ Senate seats and several House seats.",
    related: ["Crossbench", "Preferential Voting", "Senate"],
  },
  {
    term: "Preferential Voting",
    letter: "P",
    definition:
      "Australia's voting system where voters rank candidates in order of preference (1, 2, 3...). If no candidate wins a majority outright, votes from eliminated candidates are redistributed until one candidate reaches 50%+.",
    example: "Preferential voting means your vote is never 'wasted' — if your first choice loses, your vote flows to your next preference.",
    related: ["Distribution of Preferences", "Two-Party Preferred", "Informal Vote"],
  },
  {
    term: "Primary Vote",
    letter: "P",
    definition:
      "The percentage of first-preference (number 1) votes a party receives. Also called the first preference vote. A party's primary vote is often lower than their two-party preferred share.",
    example: "The ALP might receive 35% primary votes but 52% after preferences are distributed.",
    related: ["First Preference", "Two-Party Preferred", "Distribution of Preferences"],
  },
  {
    term: "Redistribution",
    letter: "R",
    definition:
      "The periodic redrawing of electorate boundaries by the AEC to ensure equal representation as populations change. Conducted by independent electoral commissioners.",
    example: "A redistribution after population growth may add new seats to Queensland while removing them from Tasmania.",
    related: ["AEC", "Electorate", "Gerrymander"],
  },
  {
    term: "Referendum",
    letter: "R",
    definition:
      "A national vote to change the Australian Constitution. Requires a double majority: a national majority AND a majority in at least 4 of 6 states. Only 8 of 44 referendums have passed in Australian history.",
    example: "The 1967 referendum to include Indigenous Australians in the census passed with 90.7% support. The 2023 Voice referendum failed.",
    related: ["Double Dissolution", "Compulsory Voting"],
  },
  {
    term: "Safe Seat",
    letter: "S",
    definition:
      "An electorate where one party has a margin of more than ~10%, making it very unlikely to change hands. Often referred to as a 'blue ribbon' (safe Liberal) or 'true blue Labor' seat.",
    example: "Cook (NSW) is a very safe Liberal seat. Grayndler (NSW) is a very safe Labor seat.",
    related: ["Marginal Seat", "Swing", "Two-Party Preferred"],
  },
  {
    term: "Senate",
    letter: "S",
    definition:
      "Australia's upper house of parliament. Each state sends 12 senators (6 at each half-Senate election); territories send 2 each. Senators serve 6-year terms (states) or 3-year terms (territories).",
    example: "The Senate reviews legislation passed by the House of Representatives and can block or amend bills.",
    related: ["Upper House", "Crossbench", "Above the Line Voting"],
  },
  {
    term: "Swing",
    letter: "S",
    definition:
      "The change in a party's vote share from one election to the next. A '3% swing to Labor' means Labor's vote increased by 3 percentage points. Swings help predict how many seats will change hands.",
    example: "In 2022, there was a 4% swing against the Coalition in key metropolitan seats.",
    related: ["Marginal Seat", "Two-Party Preferred", "Primary Vote"],
  },
  {
    term: "Teal Independents",
    letter: "T",
    definition:
      "A wave of community-backed independent candidates who won traditionally safe Liberal seats in 2022 and retained them in 2025. Named for the teal colour they adopted. Focus on climate action, integrity, and moderate progressive values.",
    example: "Allegra Spender (Wentworth), Monique Ryan (Kooyong), and Zoe Daniel (Goldstein) are prominent Teal MPs.",
    related: ["Independent", "Crossbench", "Climate Policy"],
  },
  {
    term: "Two-Party Preferred (2PP)",
    letter: "T",
    definition:
      "A measure of the final vote share between the two major parties (ALP and Coalition) after all preferences have been distributed. Used to predict seat outcomes in a two-party system.",
    example: "A 52–48 2PP to ALP means Labor wins 52% of the vote after all preferences are counted — typically leading to a majority government.",
    related: ["Preferential Voting", "Primary Vote", "Distribution of Preferences"],
  },
  {
    term: "Upper House",
    letter: "U",
    definition:
      "The Senate at the federal level, or the Legislative Council in NSW, VIC, QLD, WA, SA, and TAS. Generally uses proportional representation, resulting in more diverse representation.",
    example: "The Senate (upper house) scrutinises and can block legislation from the House of Representatives.",
    related: ["Senate", "Lower House", "House of Representatives"],
  },
  {
    term: "Writ",
    letter: "W",
    definition:
      "The formal legal document that triggers an election, issued by the Governor-General (federal) or Governor (state). The writ sets the election date, enrolment closure date, and other key dates.",
    example: "Once the Governor-General issues the election writ, the 33–38 day election campaign officially begins.",
    related: ["Governor-General", "Electoral Roll", "AEC"],
  },
];

const LETTERS = [...new Set(TERMS.map((t) => t.letter))].sort();

export default function GlossaryPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [search, setSearch] = useState("");
  const [activeLetter, setActiveLetter] = useState(null);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "#334155" : "#000000";

  const filtered = useMemo(() => {
    let terms = TERMS;
    if (search.trim()) {
      const q = search.toLowerCase();
      terms = terms.filter(
        (t) =>
          t.term.toLowerCase().includes(q) ||
          t.definition.toLowerCase().includes(q)
      );
    } else if (activeLetter) {
      terms = terms.filter((t) => t.letter === activeLetter);
    }
    return terms;
  }, [search, activeLetter]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((t) => {
      if (!groups[t.letter]) groups[t.letter] = [];
      groups[t.letter].push(t);
    });
    return groups;
  }, [filtered]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="glossary-page">
      {/* Header */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-4xl mx-auto">
          <button
            data-testid="glossary-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Political Glossary
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Know the Jargon
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            {TERMS.length} key Australian political terms explained simply. Perfect for first-time voters.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="relative mb-6" data-testid="glossary-search-container">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: textSec }} />
          <input
            type="text"
            placeholder="Search terms or definitions..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setActiveLetter(null); }}
            data-testid="glossary-search-input"
            className="w-full border-2 border-black pl-10 pr-10 py-3 font-medium text-sm focus:outline-none"
            style={{ backgroundColor: surface, color: text, boxShadow: "2px 2px 0 #000" }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              data-testid="glossary-clear-search"
            >
              <X className="w-4 h-4" style={{ color: textSec }} />
            </button>
          )}
        </div>

        {/* A-Z filter */}
        {!search && (
          <div className="flex flex-wrap gap-1 mb-8" data-testid="glossary-az-filter">
            <button
              onClick={() => setActiveLetter(null)}
              className="px-3 py-1.5 border-2 font-bold text-xs transition-all"
              style={{
                borderColor: "#000",
                backgroundColor: activeLetter === null ? "#FFC82E" : surface,
                color: activeLetter === null ? "#000" : text,
              }}
            >
              All
            </button>
            {LETTERS.map((l) => (
              <button
                key={l}
                data-testid={`glossary-letter-${l}`}
                onClick={() => setActiveLetter(activeLetter === l ? null : l)}
                className="px-3 py-1.5 border-2 font-bold text-xs transition-all hover:-translate-y-0.5"
                style={{
                  borderColor: "#000",
                  backgroundColor: activeLetter === l ? "#0F172A" : surface,
                  color: activeLetter === l ? "#FFC82E" : text,
                  boxShadow: activeLetter === l ? "2px 2px 0 #000" : "none",
                }}
              >
                {l}
              </button>
            ))}
          </div>
        )}

        {/* Results count */}
        <p className="text-sm font-bold mb-6" style={{ color: textSec }}>
          {filtered.length} {filtered.length === 1 ? "term" : "terms"}
          {search ? ` matching "${search}"` : activeLetter ? ` starting with ${activeLetter}` : " total"}
        </p>

        {filtered.length === 0 && (
          <div
            className="text-center py-16 border-2 border-black"
            style={{ backgroundColor: surface, boxShadow: "4px 4px 0 #000" }}
          >
            <p className="font-bold" style={{ color: textSec }}>
              No terms found for "{search}". Try a different search.
            </p>
          </div>
        )}

        {/* Grouped terms */}
        <div className="space-y-8">
          {Object.entries(grouped).map(([letter, terms]) => (
            <div key={letter}>
              {/* Letter heading */}
              <div
                className="inline-flex items-center justify-center w-10 h-10 border-2 border-black font-black text-xl mb-4"
                style={{ backgroundColor: "#0F172A", color: "#FFC82E", fontFamily: "Outfit, sans-serif" }}
              >
                {letter}
              </div>
              <div className="space-y-3">
                {terms.map((t) => (
                  <GlossaryCard key={t.term} term={t} surface={surface} text={text} textSec={textSec} borderC={borderC} isDark={isDark} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div
          className="mt-10 p-4 border-2 border-black text-xs"
          style={{ backgroundColor: isDark ? "#1E293B" : "#FFF9E6", color: textSec }}
        >
          <strong style={{ color: text }}>Note:</strong> This glossary is for civic education purposes. For official definitions and the most current information, visit the{" "}
          <a
            href="https://www.aec.gov.au/Learn/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold underline"
            style={{ color: "#2E5948" }}
          >
            Australian Electoral Commission
          </a>.
        </div>
      </div>
    </div>
  );
}

function GlossaryCard({ term, surface, text, textSec, borderC, isDark }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      data-testid={`glossary-term-${term.term.replace(/\s+/g, "-").toLowerCase()}`}
      className="border-2 border-black"
      style={{ backgroundColor: surface, boxShadow: "2px 2px 0 #000" }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 flex items-center justify-between gap-4 hover:bg-opacity-90 transition-colors"
        style={{ backgroundColor: "transparent" }}
      >
        <span className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif", color: text }}>
          {term.term}
        </span>
        <span
          className="text-xs font-bold px-2 py-0.5 border border-black flex-shrink-0"
          style={{ backgroundColor: expanded ? "#0F172A" : "#FFC82E", color: expanded ? "#FFC82E" : "#000" }}
        >
          {expanded ? "Less" : "More"}
        </span>
      </button>
      <div
        className="px-4 pb-1"
        style={{ borderTop: `1px solid ${isDark ? "#334155" : "#E5E7EB"}` }}
      >
        <p className="text-sm py-3 leading-relaxed" style={{ color: textSec, fontFamily: "Public Sans, sans-serif" }}>
          {term.definition}
        </p>
      </div>
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {term.example && (
            <div
              className="border-l-4 pl-3 py-1"
              style={{ borderColor: "#2E5948" }}
            >
              <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: "#2E5948" }}>
                Example
              </p>
              <p className="text-sm" style={{ color: text, fontFamily: "Public Sans, sans-serif" }}>
                {term.example}
              </p>
            </div>
          )}
          {term.related && term.related.length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: textSec }}>
                Related Terms
              </p>
              <div className="flex flex-wrap gap-1.5">
                {term.related.map((r) => (
                  <span
                    key={r}
                    className="text-xs px-2 py-0.5 border border-black font-semibold"
                    style={{ backgroundColor: isDark ? "#0F172A" : "#F3F4F6", color: text }}
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
